/**
 * Centralized PostHog analytics helpers.
 *
 * All calls are no-ops when PostHog is not initialized (i.e. REACT_APP_POSTHOG_KEY is unset).
 * This keeps call-sites clean - they never need to check if PostHog is enabled.
 */
import posthog from '../posthog';

// ---------------------------------------------------------------------------
// User identification
// ---------------------------------------------------------------------------

/**
 * Identify the current user after login.
 * Sets user properties and group (tenant) so all subsequent events
 * are attributed correctly in PostHog.
 */
export function identifyUser(user: {
  user_id: string;
  email: string;
  name?: string;
  role?: string;
}, tenant: {
  tenant_id: string;
  name: string;
}) {
  if (!posthog.__loaded) return;

  posthog.identify(user.user_id, {
    email: user.email,
    name: user.name,
    role: user.role,
    tenant_id: tenant.tenant_id,
    tenant_name: tenant.name,
  });

  posthog.group('tenant', tenant.tenant_id, {
    name: tenant.name,
  });
}

/**
 * Reset PostHog identity on logout.
 * Ensures the next session is anonymous until a new login.
 */
export function resetAnalytics() {
  if (!posthog.__loaded) return;
  posthog.reset();
}

// ---------------------------------------------------------------------------
// Custom events
// ---------------------------------------------------------------------------

/**
 * Track a custom event. No-op if PostHog is not loaded.
 */
export function trackEvent(event: string, properties?: Record<string, any>) {
  if (!posthog.__loaded) return;
  posthog.capture(event, properties);
}

// -- Copilot -----------------------------------------------------------------

export function trackCopilotMessage(threadId: string | null) {
  trackEvent('copilot_message_sent', { thread_id: threadId });
}

export function trackCopilotSQLApproval(
  action: 'approve' | 'edit' | 'reject',
  threadId: string | null,
) {
  trackEvent('copilot_sql_approval', { action, thread_id: threadId });
}

export function trackCopilotSessionStart(threadId: string | null) {
  trackEvent('copilot_session_start', { thread_id: threadId });
}

// -- Viz Chat ----------------------------------------------------------------

export function trackChatMessage(threadId: string | null) {
  trackEvent('chat_message_sent', { thread_id: threadId });
}

// -- Registration & Login ----------------------------------------------------

export function trackUserRegistered(tenantName: string, planType: string) {
  trackEvent('user_registered', { tenant_name: tenantName, plan_type: planType });
}

export function trackUserLoggedIn() {
  trackEvent('user_logged_in');
}

// -- Connectors --------------------------------------------------------------

export function trackConnectorAdded(connectorType: string) {
  trackEvent('connector_added', { connector_type: connectorType });
}

// -- Feedback ----------------------------------------------------------------

export function trackFeedbackSubmitted(
  workflowId: string,
  sentiment: 'positive' | 'negative',
) {
  trackEvent('feedback_submitted', { workflow_id: workflowId, sentiment });
}

// -- Shared Spaces -----------------------------------------------------------

export function trackInsightSavedToSpace(spaceId: string, workflowId: string) {
  trackEvent('insight_saved_to_space', { space_id: spaceId, workflow_id: workflowId });
}

// -- Dashboard ---------------------------------------------------------------

export function trackWidgetAdded(dashboardId: string) {
  trackEvent('widget_added_to_dashboard', { dashboard_id: dashboardId });
}

export function trackWidgetInsightsOpened(dashboardId: string, widgetId: string) {
  trackEvent('widget_insights_opened', { dashboard_id: dashboardId, widget_id: widgetId });
}

// -- Data Export --------------------------------------------------------------

export function trackDataExported(workflowId: string) {
  trackEvent('data_exported', { workflow_id: workflowId });
}

// -- BCL ---------------------------------------------------------------------

export function trackBCLTermAdded() {
  trackEvent('bcl_term_added');
}

// -- Self-serve onboarding wizard (/login, /onboarding) -----------------------

export function coarseErrorMessage(err: unknown): string {
  const s = err instanceof Error ? err.message : String(err);
  return s.replace(/\s+/g, ' ').trim().slice(0, 120);
}

export function trackOnboardingSignupSubmitted(props: { has_session_id: boolean }) {
  trackEvent('onboarding_signup_submitted', props);
}

export function trackOnboardingSignupFailed(props: { error_type: string }) {
  trackEvent('onboarding_signup_failed', props);
}

export function trackOnboardingOtpVerifySubmitted(props: {
  current_step: string;
  skips_to_copilot: boolean;
}) {
  trackEvent('onboarding_otp_verify_submitted', props);
}

export function trackOnboardingOtpVerifyFailed(props: { error_type: string }) {
  trackEvent('onboarding_otp_verify_failed', props);
}

export function trackOnboardingOtpResendRequested(props: { resend_count: number; max_resends?: number }) {
  trackEvent('onboarding_otp_resend_requested', props);
}

export function trackOnboardingPathSelected(props: { chosen_path: 'sample' | 'own_data' }) {
  trackEvent('onboarding_path_selected', props);
}

export function trackOnboardingPathBack(props: { prefill_path: 'sample' | 'own_data' | null }) {
  trackEvent('onboarding_path_back', props);
}

export function trackOnboardingSampleDatasetSelected(props: { dataset_id: string }) {
  trackEvent('onboarding_sample_dataset_selected', props);
}

export function trackOnboardingSampleProvisionSucceeded(props: { dataset_id: string }) {
  trackEvent('onboarding_sample_provision_succeeded', props);
}

export function trackOnboardingSampleProvisionFailed(props: { dataset_id: string; error_type: string }) {
  trackEvent('onboarding_sample_provision_failed', props);
}

export function trackOnboardingSetupProgressCompleted(props: { dataset_id: string | null }) {
  trackEvent('onboarding_setup_progress_completed', props);
}

export function trackOnboardingOwnDataBridgeContinue() {
  trackEvent('onboarding_own_data_bridge_continue');
}

export function trackOnboardingOwnDataTrySampleInstead() {
  trackEvent('onboarding_own_data_try_sample_instead');
}

/** Map verify-otp API payload to identifyUser (same as password login). */
export function identifyUserFromOnboardingVerify(
  user: {
    user_id: string;
    email: string;
    name?: string;
    role_name?: string;
  },
  tenant: { tenant_id: string; name: string },
) {
  identifyUser(
    {
      user_id: user.user_id,
      email: user.email,
      name: user.name,
      role: user.role_name,
    },
    { tenant_id: tenant.tenant_id, name: tenant.name },
  );
}

// -- Product onboarding checklist (Getting started widget) -------------------

export function trackProductOnboardingStepCompleted(props: {
  step_id: string;
  dashboard_id?: string;
}) {
  trackEvent('product_onboarding_step_completed', props);
}

export function trackProductOnboardingWidgetDismissed(props: {
  completed_count: number;
  total_count: number;
}) {
  trackEvent('product_onboarding_widget_dismissed', props);
}

export function trackProductOnboardingSkipped(props: {
  source: string;
  current_step_id?: string | null;
}) {
  trackEvent('product_onboarding_skipped', props);
}

export function trackProductOnboardingCtaClicked(props: {
  step_id: string;
  cta_route: string;
  from_path: string;
}) {
  trackEvent('product_onboarding_cta_clicked', props);
}

// -- Onboarding completion modal ("You're up and running") -------------------

export function trackOnboardingCompletionModalViewed(props: {
  is_sample_data: boolean;
  path_hint: string | null;
  clock_meaningful: boolean;
  minutes_shown: number | null;
}) {
  trackEvent('onboarding_completion_modal_viewed', props);
}

export function trackOnboardingCompletionModalDismissed(props: {
  action: 'explore' | 'backdrop' | 'escape';
}) {
  trackEvent('onboarding_completion_modal_dismissed', props);
}

export function trackOnboardingCompletionCtaClicked(props: {
  cta: 'connect_own_data' | 'view_dashboard';
  has_first_dashboard: boolean;
}) {
  trackEvent('onboarding_completion_cta_clicked', props);
}

// -- Copilot home (post-wizard) ----------------------------------------------

export function trackOnboardingCopilotStarterChipClicked(props: {
  question_index: number;
  uses_starter_questions: boolean;
}) {
  trackEvent('onboarding_copilot_starter_chip_clicked', props);
}

export function trackOnboardingCopilotPrefillSent() {
  trackEvent('onboarding_copilot_prefill_sent');
}